import { useSpinnerContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import Login from "./login";
import { useEffect, type ReactNode } from 'react';
import { useFeatureFlag, useFormSubmission } from "@hooks";
import { FeatureFlag } from '@root/enums';
import { startOAuthLogin, takeOAuthLoginResult, type OAuthError, type OAuthProvider } from '@stores';
import { Button } from '@common';
import { SSO_PROVIDERS, getSsoProviderName } from '@shared';

interface SetupLoginProps {
    initial: {
        email: string;
    };
    success: () => void;
}

const getUnknownPayorMessage = (providerName: string) => (
    <>
        Unable to sign in with {providerName}. Please contact us at{` `}
        <a className="underline" href="mailto:help@sunbound.ai">help@sunbound.ai</a>
        {` `}for assistance.
    </>
);
const getPayorMismatchMessage = (providerName: string, email: string) => (
    <>
        This {providerName} account doesn{`'`}t match the email for this signup link. Please continue with{` `}
        <span className="font-medium">{email}</span>
        {` `}or log in with your password instead.
    </>
);

const getOAuthErrorMessage = (error: OAuthError, provider: OAuthProvider, email: string): ReactNode => {
    const providerName = getSsoProviderName(provider);
    if (error === `payor_mismatch`) return getPayorMismatchMessage(providerName, email);
    if (error === `unknown_payor`) return getUnknownPayorMessage(providerName);
    return `We couldn't sign you in with ${providerName}. Please try again.`;
};

const SetupLogin = ({ initial, success }: SetupLoginProps) => {

    const { setSpinnerShow } = useSpinnerContext();
    const { email } = initial;
    const { value: isSsoEnabled } = useFeatureFlag(FeatureFlag.SSO_ENABLED);

    const { setFormState, hasFormError, formError } = useFormSubmission();
    const {
        setFormState: setSsoLoginError,
        clearFormState: clearSsoLoginError,
        hasFormError: hasSsoLoginError,
        formError: ssoLoginError
    } = useFormSubmission<ReactNode>();

    useEffect(() => {
        const oauthLoginResult = takeOAuthLoginResult();
        if (!oauthLoginResult) return;

        if (oauthLoginResult.status === `error`) {
            setSsoLoginError(getOAuthErrorMessage(oauthLoginResult.error, oauthLoginResult.provider, email));
            return;
        }

        success();
    }, [email, success, setSsoLoginError]);

    const handleSsoLogin = (provider: OAuthProvider) => {
        clearSsoLoginError();
        startOAuthLogin(provider, window.location.pathname);
    };

    const handleSubmit = async (values: { email: string; password: string }) => {

        setSpinnerShow(true);
        try {
            await sunboundPayProxy.login(values.email, values.password);
            success();
        } catch (error) {
            console.log(error);
            setFormState(`Login failed`);
        }
        setSpinnerShow(false);
    };

    return (
        <div className="flex flex-col w-full gap-6">
            <Login submit={handleSubmit} initial={{ email }}>
                <div className="border-b border-neutral-100 pb-6 mb-6 md:mb-8">
                    <h1 className="text-[20px] leading-[24px] md:text-[28px] md:leading-[40px] font-medium text-neutral-950 tracking-[0.2px] md:tracking-[0.28px]">Login to Continue Account Setup</h1>
                </div>
            </Login>
            {
                hasFormError &&
                <p className="text-red-600 text-sm mt-4 text-center">{formError}</p>
            }
            {isSsoEnabled && (
                <>
                    <div className="flex items-center gap-4">
                        <div className="flex-1 border-t border-neutral-200" />
                        <span className="text-sm text-neutral-500">or</span>
                        <div className="flex-1 border-t border-neutral-200" />
                    </div>
                    {SSO_PROVIDERS.map(({ provider, name, logo: Logo }) => (
                        <Button
                            key={provider}
                            variant="secondary"
                            size="lg"
                            className="w-full"
                            startIcon={<Logo className="mr-2" />}
                            onClick={() => handleSsoLogin(provider)}
                        >
                            Continue with {name}
                        </Button>
                    ))}
                    {
                        hasSsoLoginError &&
                        <p className="text-red-600 text-sm mt-4 text-center">{ssoLoginError}</p>
                    }
                </>
            )}
        </div>
    );

};

export default SetupLogin;
